import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { BookOpen, Upload, Trash2, Send, Music, FileText, Clock, Sparkles } from "lucide-react";
import AiAudioBox from "@/components/AiAudioBox";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-PT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }) : "—";

/* Estudo prévio: PDFs e áudio enviados automaticamente antes da sessão. */
export default function StudyPanel({ conv, onDone }) {
  const [files, setFiles] = useState(conv.study_files || []);
  const [busy, setBusy] = useState(null);
  const ai = conv.ai_audio;

  useEffect(() => {
    setFiles((cur) => {
      const have = new Set(cur.map((f) => f.token));
      const extra = (conv.study_files || []).filter((f) => !have.has(f.token));
      return extra.length ? [...cur, ...extra] : cur;
    });
  }, [conv.study_files]);
  const saved = new Set((conv.study_files || []).map((f) => f.token));

  useEffect(() => {
    if (ai?.status !== "running") return undefined;
    const t = setInterval(onDone, 6000);
    return () => clearInterval(t);
  }, [ai?.status, onDone]);

  const genAudio = async (f) => {
    try {
      await api.post(`/convocations/${conv.id}/study/ai-audio`, { file_token: f.token });
      toast.success("A gerar o resumo em áudio — demora 1 a 3 minutos");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy("upload");
    const added = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/uploads/material", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ token: r.data.token, filename: r.data.filename, kind: r.data.kind });
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setFiles((cur) => [...cur, ...added]);
    setBusy(null);
  };

  const save = async (next = files) => {
    setBusy("save");
    try {
      await api.put(`/convocations/${conv.id}/study`, { files: next });
      toast.success("Material de estudo guardado");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(null);
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      await api.put(`/convocations/${conv.id}/study`, { files });
      const r = await api.post(`/convocations/${conv.id}/study/send-now`);
      toast.success(`Estudo prévio enviado · ${r.data.emails} emails`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(null);
  };

  return (
    <div className="border-t border-[#161616] p-5 space-y-4" data-testid={`study-box-${conv.id}`}>
      <div className="flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-[#3B82F6]" />
        <div className="kw-overline flex-1">Estudo prévio · PDFs e áudio</div>
        {conv.total_count > 0 && conv.study_sent_at && (
          <span className="text-[10px] uppercase tracking-widest text-[#737373]" data-testid="study-opened-count">
            {conv.study_opened_count || 0} de {conv.total_count} já abriram
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#A3A3A3]">
        <div className="flex items-center gap-2" data-testid="study-send-at">
          <Clock className="w-3 h-3 text-[#3B82F6]" />
          {conv.study_sent_at ? <>Enviado {fmt(conv.study_sent_at)}</> : <>Envio automático: <b className="text-white">{fmt(conv.study_send_at)}</b></>}
        </div>
        <div className="flex items-center gap-2" data-testid="study-reminder-at">
          <Clock className="w-3 h-3 text-[#EAB308]" />
          {conv.study_reminder_sent_at ? <>Lembrete enviado</> : <>Lembrete a quem não abriu: <b className="text-white">{fmt(conv.study_reminder_at)}</b></>}
        </div>
      </div>
      <div className="space-y-1.5" data-testid="study-files">
        {files.map((f) => (
          <div key={f.token} className="flex items-center gap-2 text-sm bg-[#0F0F0F] border border-[#1F1F1F] px-3 py-2">
            {f.kind === "audio" ? <Music className="w-4 h-4 text-[#E50000]" /> : <FileText className="w-4 h-4 text-[#A3A3A3]" />}
            <span className="flex-1 truncate">{f.filename}</span>
            {f.kind === "pdf" && saved.has(f.token) && (
              <button onClick={() => genAudio(f)} disabled={ai?.status === "running" && Date.now() - new Date(ai.started_at).getTime() < 600000}
                className="text-[10px] uppercase tracking-widest font-bold text-[#E50000] hover:text-white inline-flex items-center gap-1 disabled:opacity-40"
                data-testid={`study-ai-audio-${f.token}`}>
                <Sparkles className="w-3 h-3" /> Gerar áudio
              </button>
            )}
            <button onClick={() => setFiles(files.filter((x) => x.token !== f.token))}
              className="text-[#555] hover:text-[#E50000]" data-testid={`study-remove-${f.token}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {files.length === 0 && <p className="text-xs text-[#555]">Ainda sem ficheiros.</p>}
      </div>
      {ai && <AiAudioBox conv={conv} ai={ai} onDone={onDone} />}
      <div className="flex flex-wrap gap-2">
        <label className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5 cursor-pointer" data-testid="study-upload-label">
          <Upload className="w-3 h-3" /> {busy === "upload" ? "A carregar…" : "Adicionar PDF ou áudio"}
          <input type="file" multiple accept=".pdf,.pptx,.docx,.mp3,.m4a,.wav,.ogg,.aac" className="hidden"
            onChange={upload} disabled={!!busy} data-testid="study-upload" />
        </label>
        <button onClick={() => save()} disabled={!!busy} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
          data-testid="study-save">Guardar</button>
        <button onClick={sendNow} disabled={!!busy || files.length === 0}
          className="kw-btn-primary !py-2 text-[10px] inline-flex items-center gap-1.5" data-testid="study-send-now">
          <Send className="w-3 h-3" /> {busy === "send" ? "A enviar…" : "Enviar já"}
        </button>
      </div>
      <p className="text-[10px] text-[#737373]">
        Sai sozinho às 9h do dia a seguir à reunião anterior (6 dias antes da sessão); se carregares depois, sai em poucos minutos.
        Os PDFs vão em anexo e em link; o áudio abre com o botão "Ouvir". PDF até 20 MB, áudio até 80 MB.
      </p>
    </div>
  );
}
